/**
 * 管理台布局壳(复刻 yqbf-next dashboard 布局):
 * 左侧边栏(180px/折叠 64px) + 右侧主列[Tabbar(Chrome 式多标签) → Toolbar(折叠按钮+面包屑+用户菜单) → 内容区]。
 * 菜单配置单一数据源见同目录 admin-menu.tsx;标签状态见 @/lib/tabbar。
 * 注意:Tailwind 无 preflight,基础元素一律显式 utility(box-border/按钮复位/链接去下划线)。
 */

import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DownOutlined,
  GlobalOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Dropdown, Tooltip } from "antd";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Fragment, useEffect, useRef, useState } from "react";
import { hasRole, useAuthStore } from "@/lib/auth";
import { useTabbarStore } from "@/lib/tabbar";
import {
  ADMIN_MENU,
  findAdminMenuItem,
  getAdminBreadcrumbs,
  isAdminMenuActive,
  normalizeAdminPath,
} from "./admin-menu";

function cx(...cls: (string | false | undefined)[]): string {
  return cls.filter(Boolean).join(" ");
}

/** 无 preflight 环境下原生 button 的复位(UA 默认灰底/边框/padding) */
const BTN_RESET = "cursor-pointer border-0 bg-transparent p-0";

export function AdminLayout() {
  const { location } = useRouterState();
  const navigate = useNavigate();
  const { loggedIn } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!loggedIn) {
      navigate({ to: "/inx/login", search: { next: location.pathname } as never });
    }
  }, [loggedIn, navigate, location.pathname]);

  if (!loggedIn) {
    return null;
  }

  return (
    <div className="box-border flex h-screen overflow-hidden">
      <Sidebar collapsed={collapsed} />

      {/* 右侧主列:Tabbar 在 Toolbar 之上 */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Tabbar />
        <Toolbar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
          <div className="flex-1 overflow-auto bg-gray-50 p-2">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// ===== 侧边栏 =====

function Sidebar({ collapsed }: { collapsed: boolean }) {
  const { location } = useRouterState();
  const menu = ADMIN_MENU.filter(
    (m) => !m.hidden && (!m.roles || m.roles.some((r) => hasRole(r))),
  );

  return (
    <aside
      className={cx(
        "box-border flex h-screen flex-shrink-0 flex-col border-r border-gray-200 bg-white text-gray-700 transition-all duration-300",
        collapsed ? "w-16" : "w-[180px]",
      )}
    >
      {/* Logo 区 h-16 居中:展开"TRUST 管理台",折叠"T" */}
      <div className="flex h-16 flex-shrink-0 items-center justify-center overflow-hidden border-b border-gray-200 px-4">
        <span className="whitespace-nowrap text-lg font-bold text-gray-900">
          {collapsed ? "T" : "TRUST 管理台"}
        </span>
      </div>

      <nav className="scrollbar-hide flex-1 overflow-y-auto py-4">
        {menu.map((item) => {
          const active = isAdminMenuActive(item, location.pathname);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cx(
                "mx-2 flex items-center overflow-hidden rounded-md px-3 py-2 text-sm no-underline transition-colors",
                collapsed ? "justify-center" : "gap-3",
                active
                  ? "bg-[#42b983] text-white hover:bg-[#359970]"
                  : "text-gray-700 hover:bg-gray-200",
              )}
            >
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-base">
                {item.icon}
              </span>
              <span
                className={cx(
                  "whitespace-nowrap transition-all duration-300",
                  collapsed ? "w-0 opacity-0" : "w-auto opacity-100",
                )}
              >
                {item.name}
              </span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

// ===== Tabbar(Chrome 式多标签) =====

function Tabbar() {
  const { location } = useRouterState();
  const navigate = useNavigate();
  const { tabs, addTab, removeTab, removeOtherTabs, removeLeftSide, removeRightSide } =
    useTabbarStore();
  const activePath = normalizeAdminPath(location.pathname);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);

  // 路由驱动自动开标签:标题从菜单扁平表反查(含 /admin/guides/new 等隐藏项)
  useEffect(() => {
    const item = findAdminMenuItem(activePath);
    if (item) {
      addTab({ path: activePath, title: item.name });
    }
  }, [activePath, addTab]);

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, tabs.length);
  }, [tabs.length]);

  // 激活标签滚动到可视区居中
  useEffect(() => {
    const activeIndex = tabs.findIndex((t) => t.path === activePath);
    if (activeIndex !== -1) {
      tabRefs.current[activeIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activePath, tabs]);

  // 纵向滚轮映射为横向滚动(原生监听,避免 passive 问题)
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY || e.deltaX;
      el.scrollBy({ left: delta > 0 ? 50 : -50 });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const closeTab = (path: string) => {
    const idx = tabs.findIndex((t) => t.path === path);
    removeTab(path);
    // 关闭激活标签 → 跳到左邻居(无左邻居取右)
    if (path === activePath) {
      const next = tabs[idx - 1] ?? tabs[idx + 1];
      if (next) {
        navigate({ href: next.path });
      }
    }
  };

  const activeIndex = tabs.findIndex((t) => t.path === activePath);

  const tabMenuPanel = (
    <div className="box-border w-[200px] rounded-md border border-gray-200 bg-white p-0 shadow-lg">
      {/* 第一行:关闭其他 / 关闭左侧 / 关闭右侧 */}
      <div className="flex gap-2 border-b border-gray-200 p-4 pb-3">
        <button
          type="button"
          title="关闭其他"
          disabled={tabs.length <= 1}
          className="flex h-6 w-6 items-center justify-center rounded border-0 bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 hover:text-[#42b983] disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            removeOtherTabs(activePath);
            setMenuOpen(false);
          }}
        >
          <CloseCircleOutlined style={{ fontSize: 14 }} />
        </button>
        <button
          type="button"
          title="关闭左侧"
          disabled={activeIndex <= 0}
          className="flex h-6 w-6 items-center justify-center rounded border-0 bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 hover:text-[#42b983] disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            removeLeftSide(activePath);
            setMenuOpen(false);
          }}
        >
          <ArrowLeftOutlined style={{ fontSize: 14 }} />
        </button>
        <button
          type="button"
          title="关闭右侧"
          disabled={activeIndex === -1 || activeIndex === tabs.length - 1}
          className="flex h-6 w-6 items-center justify-center rounded border-0 bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 hover:text-[#42b983] disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            removeRightSide(activePath);
            setMenuOpen(false);
          }}
        >
          <ArrowRightOutlined style={{ fontSize: 14 }} />
        </button>
      </div>

      {/* 标签列表(300px 可滚,行尾 hover 显 X) */}
      <div className="max-h-[300px] overflow-y-auto pb-2">
        {tabs.map((tab) => {
          const isActive = tab.path === activePath;
          return (
            <div
              key={tab.path}
              className={cx(
                "group relative mx-3 my-1 flex h-8 cursor-pointer items-center justify-between rounded px-2 text-sm transition-colors",
                isActive ? "bg-gray-200 text-gray-900" : "text-gray-700 hover:bg-gray-100",
              )}
              onClick={() => {
                navigate({ href: tab.path });
                setMenuOpen(false);
              }}
            >
              <span className="flex-1 truncate">{tab.title}</span>
              {tab.closable !== false && (
                <button
                  type="button"
                  className={cx(
                    BTN_RESET,
                    "flex h-3 w-3 flex-shrink-0 items-center justify-center text-inherit opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTab(tab.path);
                  }}
                >
                  <CloseOutlined style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="relative box-border flex flex-shrink-0 items-center gap-2 bg-gray-100 px-0 pt-2">
      {/* 标签列表:横向滚动,隐藏滚动条 */}
      <div ref={tabsRef} className="scrollbar-hide min-w-0 flex-1 overflow-x-auto pr-12">
        <div className="flex h-9 items-end overflow-visible">
          {tabs.map((tab, index) => {
            const isActive = activePath === tab.path;
            return (
              <Fragment key={tab.path}>
                <div
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  className={cx(
                    "group relative flex h-9 flex-shrink-0 items-center gap-2 px-3 text-sm transition-all",
                    isActive
                      ? "z-30 rounded-t-lg bg-white text-[#42b983] before:absolute before:bottom-0 before:left-[-8px] before:z-30 before:h-[8px] before:w-[8px] before:bg-[radial-gradient(circle_at_0_0,transparent_8px,#ffffff_8px)] before:content-[''] after:absolute after:bottom-0 after:right-[-8px] after:z-30 after:h-[8px] after:w-[8px] after:bg-[radial-gradient(circle_at_100%_0,transparent_8px,#ffffff_8px)] after:content-['']"
                      : "mb-px rounded-t-md bg-gray-100 text-gray-700 hover:bg-gray-200",
                  )}
                >
                  <span
                    className="cursor-pointer whitespace-nowrap"
                    onClick={() => navigate({ href: tab.path })}
                  >
                    {tab.title}
                  </span>
                  {tab.closable !== false && (
                    <button
                      type="button"
                      className={cx(
                        BTN_RESET,
                        "flex h-3 w-3 flex-shrink-0 items-center justify-center text-inherit opacity-60 transition-opacity hover:opacity-100",
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        closeTab(tab.path);
                      }}
                    >
                      <CloseOutlined style={{ fontSize: 12 }} />
                    </button>
                  )}
                </div>

                {/* 分隔线:仅相邻两个都未激活时显示 */}
                {!isActive && index < tabs.length - 1 && activePath !== tabs[index + 1]?.path && (
                  <div className="h-5 flex-shrink-0 self-center border-r border-gray-300" />
                )}
              </Fragment>
            );
          })}
        </div>
      </div>

      {/* 右端浮动下拉:渐隐遮罩 + ChevronDown 小按钮 */}
      {tabs.length > 1 && (
        <div className="absolute top-0 right-0 z-30 flex h-full w-12 items-center justify-center bg-gradient-to-r from-transparent to-gray-100 pl-3">
          <Dropdown
            trigger={["click"]}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            placement="bottomRight"
            popupRender={() => tabMenuPanel}
          >
            <button
              type="button"
              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors hover:border-gray-400 hover:bg-gray-50"
            >
              <DownOutlined style={{ fontSize: 12 }} />
            </button>
          </Dropdown>
        </div>
      )}
    </div>
  );
}

// ===== Toolbar(折叠按钮 + 面包屑 + 用户菜单) =====

function Toolbar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { location } = useRouterState();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [userOpen, setUserOpen] = useState(false);
  const userName = user?.real_name || user?.username || "未登录";

  const menuRow =
    "block w-full border-0 bg-transparent px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100";

  const userPanel = (
    <div className="box-border w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
      <div className="px-4 py-2">
        <p className="m-0 text-sm font-medium text-gray-900">{userName}</p>
        <p className="m-0 text-xs text-gray-500">{user?.email || user?.username || ""}</p>
      </div>
      <div className="my-1 border-t border-gray-100" />
      {/* 个人中心:独立页面未做,先指向工作台占位 */}
      <button
        type="button"
        className={cx(menuRow, "cursor-pointer text-gray-700")}
        onClick={() => {
          setUserOpen(false);
          navigate({ to: "/admin/dashboard" });
        }}
      >
        个人中心
      </button>
      <div className="my-1 border-t border-gray-100" />
      <button
        type="button"
        className={cx(menuRow, "cursor-pointer text-red-600")}
        onClick={() => {
          setUserOpen(false);
          logout();
        }}
      >
        退出登录
      </button>
    </div>
  );

  return (
    <header className="box-border flex h-12 flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onToggle}
          className={cx(
            BTN_RESET,
            "flex h-8 w-8 items-center justify-center rounded text-gray-600 transition-colors hover:bg-gray-100",
          )}
        >
          {collapsed ? (
            <MenuUnfoldOutlined style={{ fontSize: 16 }} />
          ) : (
            <MenuFoldOutlined style={{ fontSize: 16 }} />
          )}
        </button>
        <Breadcrumb pathname={location.pathname} />
      </div>

      <div className="flex items-center gap-2">
        {/* 返回门户(前台标准库) */}
        <Tooltip title="返回门户" placement="bottom">
          <Link
            to="/inx/search"
            className="flex h-8 w-8 items-center justify-center rounded text-gray-600 no-underline transition-colors hover:bg-gray-100 hover:text-[#42b983]"
          >
            <GlobalOutlined style={{ fontSize: 16 }} />
          </Link>
        </Tooltip>
        <Dropdown
          trigger={["click"]}
          open={userOpen}
          onOpenChange={setUserOpen}
          placement="bottomRight"
          popupRender={() => userPanel}
        >
          <button
            type="button"
            className={cx(
              BTN_RESET,
              "flex items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-100",
            )}
          >
            <UserOutlined style={{ fontSize: 16 }} />
            <span>{userName}</span>
          </button>
        </Dropdown>
      </div>
    </header>
  );
}

// ===== 面包屑 =====

function Breadcrumb({ pathname }: { pathname: string }) {
  const breadcrumbs = getAdminBreadcrumbs(pathname);

  return (
    <nav className="flex items-center gap-2 text-sm">
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return (
          <div key={item.path} className="flex items-center gap-2">
            {index > 0 && <RightOutlined className="text-gray-400" style={{ fontSize: 12 }} />}
            {isLast ? (
              <span className="font-medium text-gray-600">{item.name}</span>
            ) : (
              <Link
                to={item.path}
                className="flex items-center text-gray-500 no-underline transition-colors hover:text-gray-700"
              >
                {index === 0 ? <HomeOutlined style={{ fontSize: 14 }} /> : item.name}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}
